import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

export const fmtMoney = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function StatCard({ title, value, caption, icon, tone = 'primary' }) {
  const theme = useTheme();
  const palette = theme.palette[tone] || theme.palette.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        minWidth: 200,
        flex: '1 1 220px',
        borderRadius: 3,
        border: `1px solid ${alpha(palette.main, 0.18)}`,
        background: `linear-gradient(180deg, ${alpha(palette.main, 0.14)} 0%, ${alpha(palette.main, 0.04)} 100%)`,
        boxShadow: `0 18px 40px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'text.secondary' }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {caption ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            color: palette.main,
            bgcolor: alpha(palette.main, 0.13),
            border: `1px solid ${alpha(palette.main, 0.18)}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

export function MiniStat({ label, value, tone = 'primary' }) {
  const theme = useTheme();
  const palette = theme.palette[tone] || theme.palette.primary;

  return (
    <Box
      sx={{
        minWidth: 140,
        flex: '1 1 140px',
        p: 1.6,
        borderRadius: 2.5,
        bgcolor: alpha(palette.main, 0.07),
        border: `1px solid ${alpha(palette.main, 0.14)}`,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.35 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function BreakdownList({ items, emptyLabel = 'No card breakdown', tone = 'primary' }) {
  const theme = useTheme();
  const palette = theme.palette[tone] || theme.palette.primary;

  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {items.map((item) => (
        <Chip
          key={`${item.type}-${item.amount}`}
          size="small"
          sx={{
            borderRadius: 999,
            bgcolor: alpha(palette.main, 0.08),
            border: `1px solid ${alpha(palette.main, 0.15)}`,
            color: 'text.primary',
            fontWeight: 600,
          }}
          label={`${item.type}: Rs ${fmtMoney(item.amount)}`}
        />
      ))}
    </Stack>
  );
}

export function EmptyState({ title, description }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 3,
        border: '1px dashed',
        borderColor: 'divider',
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
}

export function LoadingOverlay({ open, monthLabel }) {
  const theme = useTheme();

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(theme.palette.background.default, 0.58),
        backdropFilter: 'blur(4px)',
        borderRadius: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2.5,
          minWidth: 250,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          boxShadow: `0 22px 55px ${alpha(theme.palette.common.black, 0.12)}`,
        }}
      >
        <CircularProgress size={30} thickness={4.5} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Loading {monthLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Updating all report totals for the selected month.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export function TableSection({ eyebrow, title, description, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, py: 2.2 }}>
        {eyebrow ? (
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.1 }}>
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}
